import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionIcon,
  Button,
  Group,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconFaceMask,
  IconFileExcel,
  IconPlus,
  IconSearch,
  IconX,
} from '@tabler/icons-react';

import {
  GetMedicalDoctorsRequestParams,
  MedicalDoctor,
  MedicalDoctorsSortBy,
} from '@interfaces/medicalDoctors.interface';

import { isApiError } from '@api/index';
import {
  useCountMedicalDoctorsQuery,
  useGetManyMedicalDoctorsQuery,
  useLazyGetMedicalDoctorsXlsxQuery,
} from '@api/medicalDoctors.api';
import { useUpdateUserMutation } from '@api/users.api';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertMedicalDoctorForm, {
  UPSERT_MEDICAL_DOCTOR_FORM_MODAL_ID,
} from '@components/medicalDoctors/UpsertMedicalDoctorForm';
import MedicalOrganizationsSelector from '@components/medicalOrganizations/MedicalOrganizationsSelector';
import MedicalSpecialitySelector from '@components/medicalSpecialities/MedicalSpecialitySelector';

export default function MedicalDoctorsList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreateMedicalDoctors = useUserPermissionGuard(
    'MEDICAL_DOCTORS.CREATE',
  );

  const canEditUsers = useUserPermissionGuard('USERS.UPDATE');

  const navigate = useNavigate();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetMedicalDoctorsRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'user.name',
    sortOrder: 'asc',
  });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');
  const [emailSearch, setEmailSearch, emailSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<MedicalDoctor>
  >({
    columnAccessor: 'user.name',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as MedicalDoctorsSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: medicalDoctorsCount = { count: 0 },
    isLoading: isLoadingCount,
  } = useCountMedicalDoctorsQuery({
    nameSearch: nameSearchDebounced,
    emailSearch: emailSearchDebounced,
  });

  const { data = [], isLoading } = useGetManyMedicalDoctorsQuery({
    ...filters,
    nameSearch: nameSearchDebounced,
    emailSearch: emailSearchDebounced,
  });

  const [updateUser, { isLoading: isLoadingUpdateUser }] =
    useUpdateUserMutation();

  const [getMedicalDoctorsXlsx, { isLoading: isLoadingGetMedicalDoctorsXlsx }] =
    useLazyGetMedicalDoctorsXlsxQuery();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddMedicalDoctorClick = useCallback(() => {
    openModal({
      modalId: UPSERT_MEDICAL_DOCTOR_FORM_MODAL_ID,
      title: 'Registra medico',
      children: (
        <UpsertMedicalDoctorForm
          afterSubmit={({ id }) => navigate(`/medici/${id}`)}
        />
      ),
      size: 'xl',
    });
  }, []);

  const handleExportMedicalDoctorsClick = useCallback(async () => {
    try {
      const blob = await getMedicalDoctorsXlsx().unwrap();

      const alink = document.createElement('a');
      alink.href = window.URL.createObjectURL(blob);
      alink.download = 'Medici.xlsx';
      alink.click();
      alink.remove();
    } catch (e) {
      if (isApiError(e)) {
        showNotification({
          title: 'Errore',
          message: e.data.message,
          color: 'red',
        });
      } else {
        showNotification({
          title: 'Errore inatteso',
          message: 'Si è verificato un errore.',
          color: 'red',
        });
      }
    }
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconFaceMask />
        Medici {!isLoadingCount ? `(${medicalDoctorsCount.count})` : ''}
      </Group>
    ),
    [medicalDoctorsCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () => (
      <Group>
        <Button
          leftSection={<IconFileExcel />}
          onClick={handleExportMedicalDoctorsClick}
          loading={isLoadingGetMedicalDoctorsXlsx}
        >
          Esporta medici
        </Button>
        {canCreateMedicalDoctors ? (
          <Button
            leftSection={<IconPlus />}
            onClick={handleAddMedicalDoctorClick}
          >
            Registra medico
          </Button>
        ) : (
          <></>
        )}
      </Group>
    ),
    [handleAddMedicalDoctorClick, canCreateMedicalDoctors],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        highlightOnHover
        noRecordsText="Nessun medico trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'active',
            title: 'Attivo',
            width: '0%',
            render: (record) => (
              <Tooltip
                label={
                  !canEditUsers
                    ? "Non hai i permessi per modificare l'utente"
                    : record.user.activationTokenCreatedAt
                      ? "L'utente non ha ancora attivato il suo account"
                      : record.user.active
                        ? 'Disabilita'
                        : 'Abilita'
                }
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    disabled={
                      !canEditUsers ||
                      record.user.activationTokenCreatedAt !== null ||
                      isLoadingUpdateUser
                    }
                    checked={record.user.active}
                    onChange={(e) => {
                      e.stopPropagation();
                      updateUser({
                        id: record.user.id,
                        body: { active: e.target.checked },
                      });
                    }}
                  />
                </div>
              </Tooltip>
            ),
          },
          {
            accessor: 'user.name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'user.email',
            title: 'Email',
            filter: (
              <TextInput
                placeholder="Ricerca email"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  emailSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setEmailSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'title',
            title: 'Titolo',
          },
          {
            accessor: 'speciality.name',
            title: 'Specialità',
            filter: (
              <MedicalSpecialitySelector
                multiple
                value={filters.specialityIds}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    specialityIds: value || undefined,
                  })
                }
              />
            ),
          },
          {
            accessor: 'medicalOrganizations',
            title: 'Organizzazioni mediche',
            render: (record) =>
              record.medicalOrganizations.map((mo) => mo.user.name).join(', '),
            filter: (
              <MedicalOrganizationsSelector
                multiple
                value={filters.medicalOrganizationIds}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    medicalOrganizationIds: value || undefined,
                  })
                }
              />
            ),
          },
        ]}
        records={data}
        onRowClick={({ record }) => {
          navigate(`/medici/${record.id}`);
        }}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={medicalDoctorsCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
